import React, { useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';
import { di } from 'react-magnetic-di';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import Textfield from '@atlaskit/textfield/text-field';

import { asTextValue } from '../../../../../../../../common/types/form/form-fields/field-values/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import {
	useCurrentValue,
	useFieldInvalid,
	useFieldRequired,
	useFormRendererActions,
} from '../../../../../../../../controllers/form-renderer-store';

const styles = cssMap({
	editBox: {
		maxWidth: '240px',
	},
});

export const EditTextShortInput = ({
	fieldId,
	fieldInputId,
	fieldErrorId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(useCurrentValue);
	const isInvalid = useFieldInvalid({ fieldId });

	const isRequired = useFieldRequired({ fieldId });
	const currentValue = useCurrentValue({ fieldId });
	const [textFieldValue, setTextFieldValue] = useState(currentValue);
	const { updateModifiedValue } = useFormRendererActions();

	useEffect(() => {
		setTextFieldValue(currentValue);
	}, [currentValue]);

	const debouncedUpdateModifiedValue = useMemo(
		() => debounce(updateModifiedValue, 500),
		[updateModifiedValue],
	);

	const debouncedOnChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setTextFieldValue({ text: event.target.value });
			if (event.target.value.length === 0) {
				debouncedUpdateModifiedValue(fieldId, null);
			} else {
				debouncedUpdateModifiedValue(fieldId, { text: event.target.value });
			}
		},
		[debouncedUpdateModifiedValue, fieldId],
	);

	return (
		<Box xcss={styles.editBox}>
			<Textfield
				id={fieldInputId}
				value={asTextValue(textFieldValue)?.text ?? ''}
				isInvalid={isInvalid}
				onChange={debouncedOnChange}
				width="small"
				placeholder=""
				testId={`field-input-text-short-edit-${fieldId}`}
				isRequired={isRequired}
				aria-describedby={
					isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
				}
			/>
		</Box>
	);
};
